/**
 * Records which arm of a Shopify Rollouts experiment served the session by
 * writing it to cart attributes, so it ends up on the order.
 *
 * The web pixels manager loader carries the assignment on its own script tag:
 *
 *   <script src="https://.../cdn/wpm/....js"
 *           data-rollout-ids="[9109673]"
 *           data-rollout-treatment-ids="[18251945]">
 *
 * Both are JSON arrays and are positional — index N of `data-rollout-ids` is
 * the experiment, index N of `data-rollout-treatment-ids` is the arm serving
 * this request. A session can be in more than one rollout at a time, so the
 * values are stored as comma-separated lists plus a paired `id:treatment` list
 * that keeps the association readable on the order.
 *
 * The attributes are only written once the cart holds something — an empty cart
 * never becomes an order, and writing on every pageview would create a cart
 * for every visitor.
 */
(function () {
  'use strict';

  var ROLLOUT_ATTRIBUTE = '_ab_rollout_id';
  var TREATMENT_ATTRIBUTE = '_ab_treatment_id';
  var PAIR_ATTRIBUTE = '_ab_treatment';
  var STORAGE_KEY = 'theme:abTreatmentSynced';

  // The loader is appended by the inline script in content_for_header, which
  // runs while the head is parsed — well before this deferred script. The
  // observer below only covers the case where Shopify moves that injection.
  var LOADER_SELECTOR = 'script[data-rollout-treatment-ids]';
  var LOADER_TIMEOUT = 5000;

  var routes = (window.theme && window.theme.routes) || {};
  var cartUrl = routes.cart_url || '/cart';
  var cartUpdateUrl = routes.cart_update_url || '/cart/update';
  var cartAddUrl = routes.cart_add_url || '/cart/add';

  var assignment = null;
  var inFlight = false;

  function parseIds(element, attribute) {
    var raw = element.getAttribute(attribute);
    if (!raw) return [];

    try {
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map(function (id) {
          return String(id).trim();
        })
        .filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  function readAssignment(loader) {
    var rollouts = parseIds(loader, 'data-rollout-ids');
    var treatments = parseIds(loader, 'data-rollout-treatment-ids');
    if (!treatments.length) return null;

    var pairs = treatments.map(function (treatment, index) {
      var rollout = rollouts[index];
      return rollout ? rollout + ':' + treatment : treatment;
    });

    return {
      rollouts: rollouts.join(','),
      treatments: treatments.join(','),
      pairs: pairs.join(',')
    };
  }

  function whenLoaderReady(callback) {
    var existing = document.querySelector(LOADER_SELECTOR);
    if (existing) {
      callback(existing);
      return;
    }

    if (!window.MutationObserver) return;

    var timer;
    var observer = new MutationObserver(function () {
      var loader = document.querySelector(LOADER_SELECTOR);
      if (!loader) return;

      observer.disconnect();
      clearTimeout(timer);
      callback(loader);
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    timer = setTimeout(function () {
      observer.disconnect();
      console.log('[ab-treatment] wpm loader not found within ' + LOADER_TIMEOUT + 'ms');
    }, LOADER_TIMEOUT);
  }

  function cartToken() {
    var match = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    return match ? match[1] : '';
  }

  // The cart token changes when the cart is emptied or an order completes, so
  // keying the guard on it makes the next cart re-sync on its own.
  function stamp() {
    return cartToken() + '|' + assignment.pairs;
  }

  function alreadySynced() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === stamp();
    } catch (e) {
      return false;
    }
  }

  function markSynced() {
    try {
      sessionStorage.setItem(STORAGE_KEY, stamp());
    } catch (e) {}
  }

  function matchesCart(attributes) {
    if (!attributes) return false;

    return (
      attributes[ROLLOUT_ATTRIBUTE] === assignment.rollouts &&
      attributes[TREATMENT_ATTRIBUTE] === assignment.treatments &&
      attributes[PAIR_ATTRIBUTE] === assignment.pairs
    );
  }

  function writeAttributes() {
    var attributes = {};
    attributes[ROLLOUT_ATTRIBUTE] = assignment.rollouts;
    attributes[TREATMENT_ATTRIBUTE] = assignment.treatments;
    attributes[PAIR_ATTRIBUTE] = assignment.pairs;

    return fetch(cartUpdateUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function () {
      markSynced();
    });
  }

  function sync() {
    if (!assignment || inFlight || alreadySynced()) return;
    inFlight = true;

    fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (!cart.item_count) return;
        if (matchesCart(cart.attributes)) {
          markSynced();
          return;
        }
        return writeAttributes();
      })
      .catch(function () {})
      .then(function () {
        inFlight = false;
      });
  }

  whenLoaderReady(function (loader) {
    assignment = readAssignment(loader);

    if (!assignment) {
      console.log('[ab-treatment] no rollout treatment on the wpm loader');
      return;
    }

    console.log(
      '[ab-treatment] treatment id:',
      assignment.treatments,
      '| rollout id:',
      assignment.rollouts
    );

    if (window.theme) window.theme.abTreatment = assignment;

    sync();
  });

  // Catches the first item landing in an empty cart. Both AJAX add-to-cart
  // paths in theme.js go through fetch; the non-AJAX path redirects, and the
  // sync() above picks it up on the next pageview.
  var originalFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var request = originalFetch.apply(this, arguments);

    if (url.indexOf(cartAddUrl) !== -1) {
      request
        .then(function (response) {
          if (response.ok) sync();
        })
        .catch(function () {});
    }

    return request;
  };
})();
